import { useVideoPlayer, VideoView } from "expo-video";
import { useEffect, useRef, useState } from "react";
import { Animated, Image, Platform, StyleSheet, TouchableOpacity, View } from "react-native";
import { ActivityIndicator, Icon, Text } from "react-native-paper";

import { MEDICATION_INTRO_DURATION_LABEL, MEDICATION_INTRO_VIDEO } from "@/lib/medication-media";
import { colors, fonts } from "@/lib/theme";

// ─── State 1: First Landing — No meds, no drafts ───────────────
// The how-it-works clip used to render only on Expo web — `Platform.OS === "web"`
// gated a <video> tag and native fell through to a static bottle drawing, so on
// a real phone the nurse saw no video at all. It now streams through expo-video
// on every platform, the same way the training module plays its clips
// (components/training/screens/VideoScreen.tsx).
// eslint-disable-next-line @typescript-eslint/no-require-imports
const introPoster = require("@/assets/medication-wizard/intro-poster.jpg");

export function EmptyNoMeds() {
    const floatAnim = useRef(new Animated.Value(0)).current;

    // Bundled asset — no network, so it starts instantly even with no signal.
    // Autoplays on a silent loop: no play button, nothing to tap. Muted is not
    // optional here — this plays unprompted while the nurse is standing in a
    // patient's home, so it must never make noise.
    const player = useVideoPlayer(MEDICATION_INTRO_VIDEO, (p) => {
        p.loop = true;
        p.muted = true;
        p.play();
    });

    useEffect(() => {
        Animated.loop(
            Animated.sequence([
                Animated.timing(floatAnim, { toValue: -6, duration: 1500, useNativeDriver: true }),
                Animated.timing(floatAnim, { toValue: 0, duration: 1500, useNativeDriver: true })
            ])
        ).start();
    }, [floatAnim]);

    return (
        <View style={s.noMedsContainer}>
            {/* ── Icon + copy, as a compact row at the top ──
                Was a centred illustration with the copy beneath it; the redesign
                pushes both up so the video card gets the remaining height. */}
            <View style={s.headRow}>
                <Animated.View
                    style={[s.headIcon, s.bgMint, { transform: [{ translateY: floatAnim }] }]}
                >
                    <View style={s.bottleCap} />
                    <View style={s.bottleNeck} />
                    <View style={s.bottleBody}>
                        <View style={s.bottleLabel}>
                            <View
                                style={[s.labelLine, { width: "35%", backgroundColor: "#38c9a0" }]}
                            />
                            <View style={[s.labelLine, { width: "55%" }]} />
                            <View style={[s.labelLine, { width: "45%" }]} />
                        </View>
                    </View>
                </Animated.View>
                <View style={s.headCopy}>
                    <Text style={s.headTitle}>No Medications Yet</Text>
                    <Text style={s.headSubtitle}>
                        Start by taking a photo of the patient{"'"}s medication labels, or add
                        medications manually.
                    </Text>
                </View>
            </View>

            {/* ── How-it-works video card ──
                Always playing, silent, on a loop. The poster sits underneath as
                the first-frame backstop while the decoder spins up.
                The design's play button, scrub bar and bottom caption block are
                deliberately gone: there is nothing to press any more, and the
                clip carries its own burned-in captions, which a second caption
                layer would sit on top of and fight. */}
            <View style={s.videoCard}>
                <Image source={introPoster} style={s.posterFill} resizeMode="cover" />
                <VideoView
                    player={player}
                    style={StyleSheet.absoluteFill}
                    contentFit="cover"
                    nativeControls={false}
                    accessibilityLabel="How to photograph the patient's medications"
                />

                <View style={s.howChip}>
                    <Icon source="play-circle" size={14} color={colors.teal300} />
                    <Text style={s.howChipText}>
                        HOW IT WORKS · {MEDICATION_INTRO_DURATION_LABEL}
                    </Text>
                </View>
            </View>

            <View style={s.arrowDown}>
                <View style={[s.arrowChevron, { opacity: 0.4 }]} />
                <View style={[s.arrowChevron, { opacity: 0.6 }]} />
                <View style={[s.arrowChevron, { opacity: 0.9 }]} />
            </View>
        </View>
    );
}

// ─── State 2: No saved meds, but drafts pending ────────────────
export function EmptyWithDrafts({ draftCount }: { draftCount: number }) {
    const pulseAnim = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        Animated.loop(
            Animated.sequence([
                Animated.timing(pulseAnim, {
                    toValue: 1.05,
                    duration: 1250,
                    useNativeDriver: true
                }),
                Animated.timing(pulseAnim, { toValue: 1, duration: 1250, useNativeDriver: true })
            ])
        ).start();
    }, [pulseAnim]);

    return (
        <View style={s.container}>
            <Animated.View
                style={[s.illustCircle, s.bgAmber, { transform: [{ scale: pulseAnim }] }]}
            >
                <Text style={{ fontSize: 36 }}>{"\uD83D\uDCCB"}</Text>
            </Animated.View>
            <Text style={s.title}>No Saved Medications Yet</Text>
            <Text style={s.subtitle}>
                You have{" "}
                <Text style={{ color: "#f0a030", fontWeight: "700" }}>{draftCount} items</Text>{" "}
                waiting for your review. Tap the banner above to review and save them to the
                patient's profile.
            </Text>
            <Text style={s.actionHint}>{"\u261D"} Tap "Review" above to get started</Text>
        </View>
    );
}

// ─── State 3: Search — before typing ────────────────────────────
export function SearchEmpty() {
    return (
        <View style={s.container}>
            <View style={[s.illustCircle, s.bgBlue]}>
                <View style={s.searchMag}>
                    <View style={s.searchHandle} />
                </View>
            </View>
            <Text style={[s.title, { fontSize: 14 }]}>Search Our Database</Text>
            <Text style={[s.subtitle, { fontSize: 11 }]}>
                We search <Text style={{ fontWeight: "700" }}>medications</Text> (RxNorm) and{" "}
                <Text style={{ fontWeight: "700" }}>supplements</Text> (DSLD) at the same time.
                Can't find what you're looking for? Use free text at the bottom of the results.
            </Text>
        </View>
    );
}

// ─── State 4: Search — no results ───────────────────────────────
export function SearchNoResults({ query, onFreeText }: { query: string; onFreeText: () => void }) {
    const wiggleAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.loop(
            Animated.sequence([
                Animated.timing(wiggleAnim, { toValue: -3, duration: 1000, useNativeDriver: true }),
                Animated.timing(wiggleAnim, { toValue: 3, duration: 1000, useNativeDriver: true }),
                Animated.timing(wiggleAnim, { toValue: 0, duration: 1000, useNativeDriver: true })
            ])
        ).start();
    }, [wiggleAnim]);

    return (
        <View style={s.container}>
            <Animated.View
                style={[
                    s.illustCircle,
                    s.bgAmber,
                    {
                        transform: [
                            {
                                rotate: wiggleAnim.interpolate({
                                    inputRange: [-3, 0, 3],
                                    outputRange: ["-3deg", "0deg", "3deg"]
                                })
                            }
                        ]
                    }
                ]}
            >
                <Text style={{ fontSize: 30 }}>{"\uD83E\uDD14"}</Text>
            </Animated.View>
            <Text style={[s.title, { fontSize: 14 }]}>No Results Found</Text>
            <Text style={[s.subtitle, { fontSize: 11 }]}>
                We couldn't find <Text style={{ fontWeight: "700" }}>"{query}"</Text> in our
                medication or supplement databases.
            </Text>
            <TouchableOpacity style={s.freeTextBox} onPress={onFreeText}>
                <Text style={s.freeTextBoxTitle}>{"\uD83D\uDCDD"} Type it freely</Text>
                <Text style={s.freeTextBoxHint}>
                    You'll be asked if it's a medication or a supplement
                </Text>
            </TouchableOpacity>
        </View>
    );
}

// ─── State 5: Photo extraction — zero results ───────────────────
//
// Distinct from ExtractionError below, and the distinction is the whole point:
// this is a job that RAN — the photos were read, and held nothing we could
// identify as a medication. Retrying the same photos would read the same
// nothing, so the offer here is new ones. A broken pipeline gets the other
// screen and the opposite offer.
export function ExtractionZeroResults({
    onTryAgain,
    onDismiss,
    dismissing = false
}: {
    onTryAgain: () => void;
    /**
     * Records the dismissal server-side and clears the screen. She STAYS on
     * the hub: what is behind this is the ordinary empty state, which is
     * where the three ways of adding a medication live.
     */
    onDismiss: () => void;
    dismissing?: boolean;
}) {
    return (
        <View style={[s.container, { backgroundColor: "#f8f9fa" }]}>
            <View style={[s.illustCircle, s.bgRed]}>
                <View style={{ position: "relative" }}>
                    <Text style={{ fontSize: 24, opacity: 0.4 }}>{"\uD83D\uDCF7"}</Text>
                    <Text style={{ position: "absolute", top: -4, right: -8, fontSize: 18 }}>
                        {"\u274C"}
                    </Text>
                </View>
            </View>
            <Text style={s.title}>No Medications Found</Text>
            <Text style={s.subtitle}>
                We read your photos but couldn't identify any medications or supplements in them.
                This usually means the image was blurry or the label wasn't clearly visible.
            </Text>
            <TouchableOpacity style={s.primaryBtn} onPress={onTryAgain} disabled={dismissing}>
                <Text style={s.primaryBtnText}>{"\uD83D\uDCF7"} Try Again with New Photos</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={onDismiss} disabled={dismissing}>
                <Text style={s.cancelLink}>{dismissing ? "Dismissing\u2026" : "Dismiss"}</Text>
            </TouchableOpacity>
            <View style={s.tipBox}>
                <Text style={s.tipTitle}>{"\uD83D\uDCA1"} Tips for better results:</Text>
                <Text style={s.tipText}>
                    Hold steady {"\u2022"} Good lighting {"\u2022"} Focus on the label {"\u2022"}{" "}
                    Avoid glare
                </Text>
            </View>
        </View>
    );
}

// ─── State 6: Processing failed — error ─────────────────────────
//
// The pipeline itself broke: a timeout, a dropped stream, a provider error. The
// photos are fine and still on S3, so the offer is the SAME photos again. A run
// that completed and simply found nothing gets ExtractionZeroResults above —
// telling that nurse to retry these photos is exactly the wrong advice, which
// is what she was told until Sep 2026.
export function ExtractionError({
    onRetry,
    onDismiss,
    dismissing = false
}: {
    onRetry: () => void;
    /**
     * Records the dismissal server-side and clears the screen. She STAYS on
     * the hub: what is behind this is the ordinary empty state, which is
     * where the three ways of adding a medication live.
     */
    onDismiss: () => void;
    dismissing?: boolean;
}) {
    const pulseAnim = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        Animated.loop(
            Animated.sequence([
                Animated.timing(pulseAnim, {
                    toValue: 1.05,
                    duration: 1000,
                    useNativeDriver: true
                }),
                Animated.timing(pulseAnim, { toValue: 1, duration: 1000, useNativeDriver: true })
            ])
        ).start();
    }, [pulseAnim]);

    return (
        <View style={[s.container, { backgroundColor: "#fffbfb" }]}>
            <Animated.View style={[s.errorIcon, { transform: [{ scale: pulseAnim }] }]}>
                <Text style={{ fontSize: 26 }}>{"\u26A0\uFE0F"}</Text>
            </Animated.View>
            <Text style={s.title}>Something Went Wrong</Text>
            <Text style={s.subtitle}>
                We couldn't process your photos. This might be a connection issue. Your photos are
                still saved — you can try again without retaking them.
            </Text>
            <TouchableOpacity style={s.primaryBtn} onPress={onRetry} disabled={dismissing}>
                <Text style={s.primaryBtnText}>{"\u21BB"} Try Again (same photos)</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={onDismiss} disabled={dismissing}>
                <Text style={s.cancelLink}>{dismissing ? "Dismissing\u2026" : "Dismiss"}</Text>
            </TouchableOpacity>
            <View style={s.errorTipBox}>
                <Text style={s.errorTipTitle}>Still not working?</Text>
                <Text style={s.errorTipText}>Try adding medications manually instead.</Text>
            </View>
        </View>
    );
}

// ─── State 8: Draft review — all items handled ──────────────────
export function AllDraftsComplete() {
    const pulseAnim = useRef(new Animated.Value(1)).current;
    const dotPulse = useRef(new Animated.Value(0.4)).current;

    useEffect(() => {
        Animated.loop(
            Animated.sequence([
                Animated.timing(pulseAnim, {
                    toValue: 1.05,
                    duration: 1000,
                    useNativeDriver: true
                }),
                Animated.timing(pulseAnim, { toValue: 1, duration: 1000, useNativeDriver: true })
            ])
        ).start();
        Animated.loop(
            Animated.sequence([
                Animated.timing(dotPulse, { toValue: 1, duration: 500, useNativeDriver: true }),
                Animated.timing(dotPulse, { toValue: 0.4, duration: 500, useNativeDriver: true })
            ])
        ).start();
    }, [pulseAnim, dotPulse]);

    return (
        <View style={[s.container, { backgroundColor: "#f0fff4" }]}>
            <Animated.View style={[s.checkCircle, { transform: [{ scale: pulseAnim }] }]}>
                <Text style={s.checkMark}>{"\u2713"}</Text>
            </Animated.View>
            <Text style={s.title}>All Done!</Text>
            <Text style={s.subtitle}>
                All extracted items have been reviewed. Taking you back to the medications screen...
            </Text>
            <View style={s.redirectRow}>
                <Animated.View style={[s.redirectDot, { opacity: dotPulse }]} />
                <Text style={s.redirectText}>Redirecting automatically...</Text>
            </View>
        </View>
    );
}

// ─── Processing Overlay ─────────────────────────────────────────
export function ProcessingOverlay({ onClose }: { onClose?: () => void }) {
    const spinAnim = useRef(new Animated.Value(0)).current;
    const statusIndex = useRef(0);
    const [statusText, setStatusText] = useState("Analyzing your photos...");

    useEffect(() => {
        Animated.loop(
            Animated.timing(spinAnim, { toValue: 1, duration: 2500, useNativeDriver: false })
        ).start();

        const msgs = [
            "Analyzing your photos...",
            "Reading medication labels...",
            "Identifying doses and frequencies...",
            "Preparing your review..."
        ];
        const interval = setInterval(() => {
            statusIndex.current = (statusIndex.current + 1) % msgs.length;
            setStatusText(msgs[statusIndex.current]);
        }, 3000);
        return () => clearInterval(interval);
    }, [spinAnim]);

    const spin = spinAnim.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "360deg"] });
    const spinReverse = spinAnim.interpolate({
        inputRange: [0, 1],
        outputRange: ["0deg", "-360deg"]
    });

    return (
        <View style={s.overlayContainer}>
            {/* Close button */}
            {onClose && (
                <View style={s.overlayCloseRow}>
                    <Text style={s.overlayCloseHint}>
                        Safe to close {"\u2022"} Running in background
                    </Text>
                    <TouchableOpacity style={s.overlayCloseBtn} onPress={onClose}>
                        <Text style={s.overlayCloseBtnText}>{"\u00D7"}</Text>
                    </TouchableOpacity>
                </View>
            )}

            {/* Orbiting bottle */}
            <View style={s.orbitContainer}>
                <Animated.View style={[s.orbitRing1, { transform: [{ rotate: spin }] }]} />
                <Animated.View style={[s.orbitRing2, { transform: [{ rotate: spinReverse }] }]} />
                <View style={s.orbitGlow} />
                {/* Bottle */}
                <View style={s.overlayBottleCap} />
                <View style={s.overlayBottleNeck} />
                <View style={s.overlayBottleBody}>
                    <View style={s.overlayBottleLabel}>
                        <Text style={{ fontSize: 7, fontWeight: "800", color: "#38c9a0" }}>Rx</Text>
                        <View style={[s.labelLine, { width: "35%", backgroundColor: "#38c9a0" }]} />
                        <View style={[s.labelLine, { width: "55%" }]} />
                        <View style={[s.labelLine, { width: "45%" }]} />
                    </View>
                </View>
            </View>

            <Text style={s.overlayStatusText}>{statusText}</Text>
            <Text style={s.overlaySubText}>Our AI is extracting your medications</Text>

            {/* Progress indicator */}
            <ActivityIndicator size="large" color="#38c9a0" style={{ marginTop: 20 }} />
        </View>
    );
}

// ─── Processing Banner (for subsequent uploads when meds already exist on hub) ──
export function ProcessingBanner({
    /** What started this run, so the copy matches. Null when we did not start it. */
    source = null
}: {
    source?: "photo" | "voice" | null;
} = {}) {
    const bounceAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const bounce = Animated.loop(
            Animated.sequence([
                Animated.timing(bounceAnim, {
                    toValue: -4,
                    duration: 800,
                    useNativeDriver: Platform.OS !== "web"
                }),
                Animated.timing(bounceAnim, {
                    toValue: 0,
                    duration: 800,
                    useNativeDriver: Platform.OS !== "web"
                })
            ])
        );
        bounce.start();
        return () => bounce.stop();
    }, [bounceAnim]);

    return (
        <View style={s.processingBanner}>
            <Animated.View style={{ transform: [{ translateY: bounceAnim }] }}>
                <Icon source="pill" size={22} color={colors.teal600} />
            </Animated.View>
            <View style={{ flex: 1 }}>
                <Text style={s.processingBannerTitle}>
                    {source === "voice"
                        ? "Reading what you said..."
                        : source === "photo"
                          ? "Processing your photos..."
                          : "Reading your medications..."}
                </Text>
                <Text style={s.processingBannerSubtext}>New items will appear shortly</Text>
            </View>
        </View>
    );
}

const s = StyleSheet.create({
    // ── Redesigned "no medications" state (icon+copy row, then video card) ──
    noMedsContainer: {
        flex: 1,
        paddingTop: 18,
        paddingHorizontal: 20
    },
    headRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 14,
        flexGrow: 0,
        flexShrink: 0
    },
    headIcon: {
        width: 68,
        height: 68,
        borderRadius: 34,
        alignItems: "center",
        justifyContent: "center",
        flexGrow: 0,
        flexShrink: 0
    },
    headCopy: {
        flex: 1,
        minWidth: 0
    },
    headTitle: {
        fontSize: 16,
        fontFamily: fonts.extraBold,
        color: "#1F1547"
    },
    headSubtitle: {
        marginTop: 4,
        fontSize: 12,
        fontFamily: fonts.regular,
        color: "#a0aec0",
        lineHeight: 18
    },
    videoCard: {
        marginTop: 16,
        flex: 1,
        minHeight: 0,
        borderRadius: 20,
        overflow: "hidden",
        backgroundColor: "#0B0B1F",
        shadowColor: "#0F0A28",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.12,
        shadowRadius: 26,
        elevation: 6
    },
    posterFill: {
        ...StyleSheet.absoluteFill,
        width: "100%",
        height: "100%",
        // RN Web ignores the deprecated `resizeMode` prop in some builds; the
        // style form applies on both web and native.
        resizeMode: "cover"
    },
    videoScrim: {
        ...StyleSheet.absoluteFill,
        backgroundColor: "rgba(11,11,31,0.34)"
    },
    howChip: {
        position: "absolute",
        top: 12,
        left: 12,
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingVertical: 5,
        paddingHorizontal: 10,
        borderRadius: 9999,
        backgroundColor: "rgba(11,11,31,0.55)"
    },
    howChipText: {
        fontSize: 10,
        fontFamily: fonts.extraBold,
        color: "#FFFFFF",
        letterSpacing: 0.6
    },
    playWrap: {
        ...StyleSheet.absoluteFill,
        alignItems: "center",
        justifyContent: "center"
    },
    playBtn: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: "rgba(255,255,255,0.94)",
        alignItems: "center",
        justifyContent: "center",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.28,
        shadowRadius: 26,
        elevation: 8
    },
    videoCaption: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        paddingTop: 14,
        paddingHorizontal: 16,
        paddingBottom: 16
    },
    videoCaptionTitle: {
        fontSize: 15,
        fontFamily: fonts.extraBold,
        color: "#FFFFFF",
        lineHeight: 20
    },
    videoCaptionSub: {
        marginTop: 3,
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: "rgba(255,255,255,0.72)"
    },
    scrubTrack: {
        marginTop: 9,
        height: 3,
        borderRadius: 9999,
        backgroundColor: "rgba(255,255,255,0.28)"
    },
    scrubFill: {
        width: "0%",
        height: "100%",
        borderRadius: 9999,
        backgroundColor: colors.teal400
    },
    container: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: 28,
        textAlign: "center"
    },
    videoWrap: {
        marginBottom: 20,
        borderRadius: 20,
        overflow: "hidden",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 4
    },
    illustCircle: {
        width: 100,
        height: 100,
        borderRadius: 50,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 18
    },
    bgMint: { backgroundColor: "#f0fdf9" },
    bgAmber: { backgroundColor: "#fffbeb" },
    bgBlue: { backgroundColor: "#f0f4ff" },
    bgRed: { backgroundColor: "#fff5f5" },
    title: {
        fontSize: 16,
        fontWeight: "800",
        color: "#1F1547",
        marginBottom: 6,
        textAlign: "center"
    },
    subtitle: {
        fontSize: 12,
        color: "#a0aec0",
        lineHeight: 18,
        textAlign: "center"
    },
    actionHint: {
        fontSize: 11,
        color: "#38c9a0",
        fontWeight: "700",
        marginTop: 10
    },
    // Bottle
    bottleCap: {
        width: 24,
        height: 8,
        backgroundColor: "#48b898",
        borderTopLeftRadius: 3,
        borderTopRightRadius: 3,
        marginBottom: -2
    },
    bottleNeck: {
        width: 20,
        height: 10,
        backgroundColor: "#f7b84e",
        borderTopLeftRadius: 2,
        borderTopRightRadius: 2,
        marginBottom: -2
    },
    bottleBody: {
        width: 36,
        height: 54,
        backgroundColor: "#f7b84e",
        borderRadius: 6,
        alignItems: "center",
        justifyContent: "center"
    },
    bottleLabel: {
        width: 28,
        height: 24,
        backgroundColor: "#FFFFFF",
        borderRadius: 3,
        alignItems: "center",
        justifyContent: "center",
        gap: 2
    },
    labelLine: {
        height: 2,
        width: "55%",
        backgroundColor: "#e2e8f0",
        borderRadius: 1
    },
    // Arrow
    arrowDown: {
        marginTop: 10,
        alignItems: "center",
        gap: 2
    },
    arrowChevron: {
        width: 10,
        height: 10,
        borderRightWidth: 2,
        borderBottomWidth: 2,
        borderColor: "#38c9a0",
        transform: [{ rotate: "45deg" }]
    },
    // Search magnifier
    searchMag: {
        width: 48,
        height: 48,
        borderWidth: 3,
        borderColor: "#e2e8f0",
        borderRadius: 24
    },
    searchHandle: {
        position: "absolute",
        bottom: -8,
        right: -8,
        width: 4,
        height: 16,
        backgroundColor: "#cbd5e0",
        borderRadius: 2,
        transform: [{ rotate: "-45deg" }]
    },
    // Free text box
    freeTextBox: {
        marginTop: 14,
        width: "100%",
        padding: 12,
        backgroundColor: "#f8f9fa",
        borderWidth: 2,
        borderStyle: "dashed",
        borderColor: "#38c9a0",
        borderRadius: 10,
        alignItems: "center"
    },
    freeTextBoxTitle: {
        fontSize: 13,
        fontWeight: "700",
        color: "#38c9a0"
    },
    freeTextBoxHint: {
        fontSize: 10,
        color: "#a0aec0",
        marginTop: 3
    },
    // Buttons
    primaryBtn: {
        backgroundColor: "#38c9a0",
        paddingVertical: 10,
        paddingHorizontal: 20,
        borderRadius: 12,
        marginTop: 16,
        width: "100%",
        alignItems: "center"
    },
    primaryBtnText: {
        fontSize: 12,
        fontWeight: "700",
        color: "#FFFFFF"
    },
    cancelLink: {
        fontSize: 12,
        color: "#718096",
        fontWeight: "500",
        marginTop: 10
    },
    // Tips
    tipBox: {
        marginTop: 14,
        padding: 10,
        backgroundColor: "#f0f4ff",
        borderRadius: 8,
        width: "100%"
    },
    tipTitle: {
        fontSize: 10,
        color: "#4361ee",
        fontWeight: "600"
    },
    tipText: {
        fontSize: 10,
        color: "#718096",
        marginTop: 3,
        lineHeight: 15
    },
    // Error
    errorIcon: {
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: "#fff5f5",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 18
    },
    errorTipBox: {
        marginTop: 14,
        padding: 10,
        backgroundColor: "#fff5f5",
        borderWidth: 1,
        borderColor: "#fed7d7",
        borderRadius: 8,
        width: "100%"
    },
    errorTipTitle: { fontSize: 10, color: "#e53e3e", fontWeight: "600" },
    errorTipText: { fontSize: 10, color: "#718096", marginTop: 3 },
    // Check circle
    checkCircle: {
        width: 72,
        height: 72,
        borderRadius: 36,
        backgroundColor: "#f0fff4",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 18
    },
    checkMark: {
        fontSize: 32,
        fontWeight: "800",
        color: "#38c9a0"
    },
    redirectRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginTop: 16
    },
    redirectDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: "#38c9a0"
    },
    redirectText: {
        fontSize: 11,
        color: "#38c9a0",
        fontWeight: "600"
    },
    // Processing overlay
    overlayContainer: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(26,26,46,0.75)",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
        padding: 20
    },
    overlayCloseRow: {
        position: "absolute",
        top: 14,
        right: 14,
        flexDirection: "row",
        alignItems: "center",
        gap: 6
    },
    overlayCloseHint: {
        fontSize: 9,
        color: "rgba(255,255,255,0.6)",
        fontWeight: "500"
    },
    overlayCloseBtn: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: "rgba(255,255,255,0.15)",
        alignItems: "center",
        justifyContent: "center"
    },
    overlayCloseBtnText: {
        color: "#FFFFFF",
        fontSize: 14,
        fontWeight: "600"
    },
    orbitContainer: {
        width: 160,
        height: 160,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 16
    },
    orbitRing1: {
        position: "absolute",
        width: 110,
        height: 110,
        borderRadius: 55,
        borderWidth: 2.5,
        borderColor: "transparent",
        borderTopColor: "rgba(107,70,193,0.5)",
        borderRightColor: "rgba(56,201,160,0.35)"
    },
    orbitRing2: {
        position: "absolute",
        width: 140,
        height: 140,
        borderRadius: 70,
        borderWidth: 2.5,
        borderColor: "transparent",
        borderBottomColor: "rgba(67,97,238,0.4)",
        borderLeftColor: "rgba(245,166,35,0.3)"
    },
    orbitGlow: {
        position: "absolute",
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: "rgba(107,70,193,0.12)"
    },
    overlayBottleCap: {
        width: 32,
        height: 10,
        backgroundColor: "#48b898",
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        marginBottom: -2,
        zIndex: 2
    },
    overlayBottleNeck: {
        width: 24,
        height: 10,
        backgroundColor: "#f7b84e",
        borderTopLeftRadius: 2,
        borderTopRightRadius: 2,
        marginBottom: -2,
        zIndex: 2
    },
    overlayBottleBody: {
        width: 52,
        height: 78,
        backgroundColor: "#f7b84e",
        borderRadius: 8,
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2
    },
    overlayBottleLabel: {
        width: 40,
        height: 38,
        backgroundColor: "#FFFFFF",
        borderRadius: 3,
        alignItems: "center",
        justifyContent: "center",
        gap: 2
    },
    overlayStatusText: {
        fontSize: 15,
        fontWeight: "700",
        color: "#FFFFFF",
        marginBottom: 4
    },
    overlaySubText: {
        fontSize: 11,
        color: "rgba(255,255,255,0.5)",
        marginBottom: 14
    },
    overlayProgressBg: {
        width: 160,
        height: 3,
        backgroundColor: "rgba(255,255,255,0.15)",
        borderRadius: 3,
        overflow: "hidden"
    },

    // Processing Banner
    processingBanner: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        marginHorizontal: 20,
        marginTop: 12,
        marginBottom: 4,
        padding: 14,
        borderRadius: 14,
        backgroundColor: "#f0fdf9",
        borderWidth: 1,
        borderColor: "#CCFBEF"
    },
    processingBannerTitle: {
        fontSize: 13,
        fontWeight: "700",
        color: "#14957D"
    },
    processingBannerSubtext: {
        fontSize: 11,
        color: "#6B6B8D",
        marginTop: 2
    }
});
